// ============================================================
// dev-core-web/src/lib/api.ts
// Version: 1.1.0
// Purpose:
//   - единый HTTP-клиент dev-core-web → dev-core API
//   - envelope { data, meta } для успеха (API.md §6.1, §6.2)
//   - envelope { error } для ошибки (API.md §6.4)
//   - заголовки X-Request-Id, Idempotency-Key, traceparent (API.md §3.3)
//   - НЕ содержит бизнес-логики, не знает про RBAC/Classification
//   - server-side всё равно проверит RBAC/Classification/Approval
//
// Ссылки:
//   - docs/API.md §3, §6, §17
//   - docs/COPILOT.md §18
//   - docs/CLASSIFICATION.md §5
//   - docs/RBAC.md §16
// ============================================================

// ------------------------------------------------------------
// Базовые типы (API.md §6)
// ------------------------------------------------------------

export interface ApiMetaPagination {
  limit: number;
  nextCursor?: string;
  hasMore: boolean;
}

export interface ApiMeta {
  requestId: string;
  pagination?: ApiMetaPagination;
}

export interface ApiSuccess<T> {
  data: T;
  meta: ApiMeta;
}

export interface ApiErrorBody {
  code: ApiErrorCode;
  message: string;
  requestId: string;
  retryable?: boolean;
  details?: Record<string, unknown> | undefined;
}

export interface ApiErrorEnvelope {
  error: ApiErrorBody;
}

// Публичные коды ошибок (API.md §17.1)
export type ApiErrorCode =
  // Auth / access
  | 'UNAUTHENTICATED'
  | 'MFA_REQUIRED'
  | 'MFA_INVALID'
  | 'MFA_CHALLENGE_EXPIRED'
  | 'MFA_ENROLMENT_REQUIRED'
  | 'PERMISSION_DENIED'
  | 'SCOPE_REQUIRED'
  | 'CLASSIFICATION_DENIED'
  // Input / query
  | 'INVALID_INPUT'
  | 'VALIDATION_FAILED'
  | 'INVALID_QUERY'
  // Resource
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'DUPLICATE'
  | 'GONE'
  | 'PRECONDITION_REQUIRED'
  | 'RESOURCE_VERSION_CONFLICT'
  // Idempotency
  | 'IDEMPOTENCY_KEY_REUSED'
  | 'IDEMPOTENCY_KEY_MISSING'
  // Rate limiting
  | 'RATE_LIMITED'
  | 'QUOTA_EXCEEDED'
  | 'PAYLOAD_TOO_LARGE'
  | 'UNSUPPORTED_MEDIA_TYPE'
  // Actions
  | 'ACTION_NOT_FOUND'
  | 'APPROVAL_REQUIRED'
  | 'APPROVAL_DENIED'
  | 'APPROVAL_EXPIRED'
  | 'INVALID_EXECUTION_TRANSITION'
  | 'ACTION_TIMEOUT'
  | 'ACTION_ABORTED'
  | 'ACTION_CANCELLED'
  | 'ACTION_FAILED'
  | 'SNAPSHOT_FAILED'
  | 'ROLLBACK_FAILED'
  // Copilot (COPILOT.md §18.4)
  | 'COPILOT_UNAVAILABLE'
  | 'COPILOT_BUDGET_EXCEEDED'
  | 'COPILOT_INJECTION_DETECTED'
  | 'COPILOT_POLICY_VIOLATED'
  | 'COPILOT_TOOL_DENIED'
  | 'COPILOT_OUTPUT_BLOCKED'
  | 'COPILOT_SESSION_LIMIT'
  // Infra
  | 'DEPENDENCY_UNAVAILABLE'
  | 'SERVICE_DEGRADED'
  | 'UPSTREAM_TIMEOUT'
  | 'INTERNAL_ERROR';

// ------------------------------------------------------------
// Типы Copilot (COPILOT.md §11.2, §18.3)
// ------------------------------------------------------------

export interface ClassificationLabel {
  level: 'PUBLIC' | 'INTERNAL' | 'CONFIDENTIAL' | 'SECRET' | 'TOP_SECRET';
  categories: string[];
}

export interface Citation {
  source: string;
  id: string;
  classification: ClassificationLabel;
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
}

export interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  classification?: ClassificationLabel;
  citations?: Citation[];
  toolCalls?: CopilotToolCall[];
}

export interface CopilotToolCall {
  id: string;
  toolName: string;
  actionKey: string;
  executionId?: string;
  status:
    | 'proposed'
    | 'pending_approval'
    | 'queued'
    | 'running'
    | 'succeeded'
    | 'failed'
    | 'denied';
}

export interface CopilotSuggestion {
  id: string;
  title: string;
  description?: string;
  actionKey?: string;
  riskHint?: 'read' | 'low' | 'elevated' | 'critical';
}

export interface CopilotChatRequest {
  sessionId?: string | undefined;
  message: string;
  context?: {
    entityId?: string | undefined;
    incidentId?: string | undefined;
    scope?: { resource: string; id: string } | undefined;
  } | undefined;
}

export interface CopilotChatResponse {
  answer: string;
  citations?: Citation[];
  classification: ClassificationLabel;
  model: string;
  usage: TokenUsage;
  requestId: string;
  sessionId?: string;
}

// ------------------------------------------------------------
// Ошибка API
// ------------------------------------------------------------

export class ApiError extends Error {
  public readonly code: ApiErrorCode;
  public readonly status: number;
  public readonly requestId: string;
  public readonly retryable: boolean;
  public readonly details?: Record<string, unknown> | undefined;

  constructor(params: {
    code: ApiErrorCode;
    message: string;
    status: number;
    requestId: string;
    retryable: boolean;
    details?: Record<string, unknown> | undefined;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.requestId = params.requestId;
    this.retryable = params.retryable;
    this.details = params.details;
  }
}

// ------------------------------------------------------------
// Конфигурация
// ------------------------------------------------------------

const API_BASE = '/api/v1';

// Токен читаем из authStore (Zustand). Импорт через отдельный
// accessor, чтобы не создавать циклическую зависимость.
type TokenProvider = () => string | null;
let tokenProvider: TokenProvider = () => null;

export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

// Refresh-хук (authStore.refresh) — тем же способом, что и токен:
// api.ts не импортирует store напрямую, чтобы не было цикла.
type RefreshHandler = () => Promise<void>;
let refreshHandler: RefreshHandler | null = null;

export function setRefreshHandler(handler: RefreshHandler | null): void {
  refreshHandler = handler;
}

// Эти пути не ретраим через refresh: иначе POST /auth/refresh,
// вернувший 401 (нет/отозвана cookie), ушёл бы в рекурсию.
const REFRESH_EXCLUDED = new Set([
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  // MFA (ADR-0008): 401 MFA_REQUIRED не должен триггерить refresh-loop
  // и стирать challenge
  '/auth/mfa/verify',
  '/auth/mfa/enrol',
  '/auth/webauthn/login/verify',
]);

// ------------------------------------------------------------
// Утилиты
// ------------------------------------------------------------

function generateRequestId(): string {
  // req_<ulid-like>. Точная ULID не критична на клиенте — сервер
  // всё равно вернёт фактический requestId в meta.
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 12);
  return `req_${ts}${rand}`;
}

function buildHeaders(opts: {
  hasBody: boolean;
  idempotencyKey?: string | undefined;
}): HeadersInit {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': generateRequestId(),
  };

  if (opts.hasBody) {
    headers['Content-Type'] = 'application/json; charset=utf-8';
  }

  const token = tokenProvider();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (opts.idempotencyKey) {
    headers['Idempotency-Key'] = opts.idempotencyKey;
  }

  return headers;
}

// ------------------------------------------------------------
// Ядро запроса
// ------------------------------------------------------------

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string | undefined;
  signal?: AbortSignal | undefined;
  query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  isRetry = false,
): Promise<T> {
  const method = options.method ?? 'GET';
  const hasBody = options.body !== undefined;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers: buildHeaders({
        hasBody,
        idempotencyKey: options.idempotencyKey,
      }),
      body: hasBody ? JSON.stringify(options.body) : null,
      signal: options.signal ?? null,
      credentials: 'same-origin',
    });
  } catch (err) {
    // Сетевой сбой (до получения ответа). Это локальная ошибка,
    // не ApiError — вызывающий код решает, что делать.
    throw new ApiError({
      code: 'DEPENDENCY_UNAVAILABLE',
      message: err instanceof Error ? err.message : 'Network error',
      status: 0,
      requestId: '',
      retryable: true,
    });
  }

  // 204 No Content — успех без тела.
  if (response.status === 204) {
    return undefined as T;
  }

  let payload: unknown;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      payload = await response.json();
    } catch {
      throw new ApiError({
        code: 'INTERNAL_ERROR',
        message: 'Invalid JSON response',
        status: response.status,
        requestId: response.headers.get('X-Request-Id') ?? '',
        retryable: false,
      });
    }
  }

  if (!response.ok) {
    const env = payload as ApiErrorEnvelope | undefined;
    const errorBody = env?.error;
    const apiError = new ApiError({
      code: errorBody?.code ?? 'INTERNAL_ERROR',
      message: errorBody?.message ?? `HTTP ${response.status}`,
      status: response.status,
      requestId:
        errorBody?.requestId ??
        response.headers.get('X-Request-Id') ??
        '',
      retryable: errorBody?.retryable ?? false,
      details: errorBody?.details,
    });

    // 401 → один раз обновить access token и повторить запрос.
    // Так переживаем и cold reload, и истечение access TTL.
    if (
      response.status === 401 &&
      !isRetry &&
      refreshHandler !== null &&
      !REFRESH_EXCLUDED.has(path)
    ) {
      try {
        await refreshHandler();
      } catch (refreshErr) {
        // 401/403 от refresh = сессии нет → отдаём исходный 401,
        // его обработает queryClient (разлогин).
        // Сетевой/5xx сбой refresh — НЕ повод разлогинивать:
        // пробрасываем его как есть.
        const refreshStatus = (refreshErr as { status?: number }).status;
        if (refreshStatus !== 401 && refreshStatus !== 403) {
          throw refreshErr;
        }
        throw apiError;
      }

      if (tokenProvider() !== null) {
        return request<T>(path, options, true);
      }
    }

    throw apiError;
  }

  return (payload as ApiSuccess<T>).data;
}

// ------------------------------------------------------------
// Публичный API-клиент
// ------------------------------------------------------------

export const api = {
  get<T>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return request<T>(path, { ...options, method: 'GET' });
  },

  post<T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return request<T>(path, { ...options, method: 'POST', body });
  },

  put<T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return request<T>(path, { ...options, method: 'PUT', body });
  },

  patch<T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return request<T>(path, { ...options, method: 'PATCH', body });
  },

  delete<T>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return request<T>(path, { ...options, method: 'DELETE' });
  },
};

// ------------------------------------------------------------
// Copilot API (COPILOT.md §18)
// ------------------------------------------------------------

export const copilotApi = {
  chat(body: CopilotChatRequest, signal?: AbortSignal): Promise<CopilotChatResponse> {
    return api.post<CopilotChatResponse>('/copilot/chat', body, { signal });
  },

  listSessions(): Promise<unknown[]> {
    return api.get<unknown[]>('/copilot/sessions');
  },

  getSession(id: string): Promise<unknown> {
    return api.get<unknown>(`/copilot/sessions/${encodeURIComponent(id)}`);
  },

  deleteSession(id: string): Promise<void> {
    return api.delete<void>(`/copilot/sessions/${encodeURIComponent(id)}`);
  },

  listTools(): Promise<unknown[]> {
    return api.get<unknown[]>('/copilot/tools');
  },

  listModels(): Promise<unknown[]> {
    return api.get<unknown[]>('/copilot/models');
  },
};